import React, { useMemo, useState } from 'react';
import {
    ArrowUpRight,
    Calendar as CalendarIcon,
    Check,
    ChevronLeft,
    ChevronRight,
    Download,
    ExternalLink,
    Plus,
    Sparkles,
    Trash2,
    X
} from 'lucide-react';
import { Link } from 'react-router';
import ScreenLayout from './ScreenLayout';
import { prayerPlans } from '../data/prayerPlans';
import {
    addCalendarEvent,
    downloadIcsCalendar,
    fromDateKey,
    getCalendarEvents,
    getGoogleCalendarUrl,
    removeCalendarEvent,
    removePrayerPlan,
    schedulePrayerPlan,
    toDateKey
} from '../services/prayerCalendarService';

const todayKey = () => toDateKey(new Date());

const Calendar = () => {
    const [events, setEvents] = useState(getCalendarEvents);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [showPlans, setShowPlans] = useState(false);
    const [planStart, setPlanStart] = useState(todayKey());
    const [newEvent, setNewEvent] = useState({ title: '', description: '', scripture: '' });
    const [installedPlan, setInstalledPlan] = useState('');

    const eventsByDate = useMemo(() => events.reduce((grouped, event) => ({
        ...grouped,
        [event.date]: [...(grouped[event.date] || []), event]
    }), {}), [events]);
    const upcomingEvents = events
        .filter((event) => event.date >= todayKey())
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(0, 6);
    const activePlanIds = [...new Set(events.filter((event) => event.planId).map((event) => event.planId))];

    const installPlan = (planId) => {
        setEvents(schedulePrayerPlan(planId, fromDateKey(planStart)));
        setInstalledPlan(planId);
        window.setTimeout(() => setInstalledPlan(''), 2500);
    };

    const addPersonalEvent = () => {
        if (!selectedDate || !newEvent.title.trim()) return;
        setEvents(addCalendarEvent({ ...newEvent, date: selectedDate }));
        setNewEvent({ title: '', description: '', scripture: '' });
    };

    const deleteEvent = (eventId) => setEvents(removeCalendarEvent(eventId));

    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(currentMonth);
    const monthDays = [
        ...Array.from({ length: firstDay }, (_, index) => ({ empty: true, key: `empty-${index}` })),
        ...Array.from({ length: daysInMonth }, (_, index) => {
            const day = index + 1;
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day, 12);
            return { day, dateKey: toDateKey(date), isToday: toDateKey(date) === todayKey() };
        })
    ];

    return (
        <ScreenLayout title="Prayer Calendar" icon={CalendarIcon}>
            <section className="calendar-hero">
                <div>
                    <p className="shared-kicker"><Sparkles size={14} /> Build a gentle rhythm</p>
                    <h1>Give prayer a place in your days.</h1>
                    <p>Add your own focus or begin with a ready-made plan. Your calendar can be full without feeling busy.</p>
                </div>
                <div className="calendar-hero-actions">
                    <button onClick={() => setShowPlans(true)}><Plus size={18} /> Add a prayer plan</button>
                    <button onClick={() => downloadIcsCalendar(events)} disabled={!events.length}><Download size={18} /> Export for Google Calendar</button>
                </div>
            </section>

            <section className="calendar-plan-strip">
                <header><div><p className="shared-kicker">Ready-made rhythms</p><h2>Start with a prayer plan</h2></div><button onClick={() => setShowPlans(true)}>See all plans <ArrowUpRight size={16} /></button></header>
                <div>
                    {prayerPlans.map((plan) => (
                        <button className={`calendar-plan-mini calendar-plan-${plan.tone}`} key={plan.id} onClick={() => setShowPlans(true)}>
                            <span>{plan.duration} days</span><strong>{plan.title}</strong><small>{activePlanIds.includes(plan.id) ? 'On your calendar' : plan.description}</small>
                        </button>
                    ))}
                </div>
            </section>

            <div className="calendar-layout">
                <section className="prayer-calendar-card">
                    <header>
                        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} aria-label="Previous month"><ChevronLeft size={20} /></button>
                        <div><small>Your prayer calendar</small><h2>{monthLabel}</h2></div>
                        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} aria-label="Next month"><ChevronRight size={20} /></button>
                    </header>
                    <div className="prayer-calendar-grid">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => <span className="prayer-calendar-weekday" key={day}>{day}</span>)}
                        {monthDays.map((day) => day.empty ? <span key={day.key} /> : (
                            <button
                                key={day.dateKey}
                                className={`${day.isToday ? 'is-today' : ''} ${eventsByDate[day.dateKey]?.length ? 'has-events' : ''}`}
                                onClick={() => setSelectedDate(day.dateKey)}
                            >
                                <strong>{day.day}</strong>
                                <span>
                                    {(eventsByDate[day.dateKey] || []).slice(0, 2).map((event) => <i key={event.id}>{event.title}</i>)}
                                </span>
                                {(eventsByDate[day.dateKey]?.length || 0) > 2 && <small>+{eventsByDate[day.dateKey].length - 2} more</small>}
                            </button>
                        ))}
                    </div>
                </section>

                <aside className="calendar-upcoming">
                    <header><p className="shared-kicker">Coming up</p><h2>Your next prayers</h2></header>
                    <div>
                        {upcomingEvents.length ? upcomingEvents.map((event) => (
                            <button key={event.id} onClick={() => setSelectedDate(event.date)}>
                                <time><strong>{fromDateKey(event.date).getDate()}</strong><span>{new Intl.DateTimeFormat('en-US', { month: 'short' }).format(fromDateKey(event.date))}</span></time>
                                <span><strong>{event.title}</strong><small>{event.scripture || event.description}</small></span>
                                <ChevronRight size={17} />
                            </button>
                        )) : <div className="calendar-upcoming-empty"><CalendarIcon size={25} /><p>Add a prayer plan or choose a day to begin.</p></div>}
                    </div>
                    <Link to="/journal"><Sparkles size={17} /> Open your prayer journal</Link>
                </aside>
            </div>

            {showPlans && (
                <div className="calendar-overlay" onClick={() => setShowPlans(false)}>
                    <section className="calendar-plan-sheet" onClick={(event) => event.stopPropagation()}>
                        <header><div><small>Fill your calendar</small><h2>Choose a prayer plan</h2></div><button onClick={() => setShowPlans(false)}><X size={20} /></button></header>
                        <label>Start date<input type="date" value={planStart} onChange={(event) => setPlanStart(event.target.value)} /></label>
                        <div className="calendar-plan-options">
                            {prayerPlans.map((plan) => (
                                <article className={`calendar-plan-option calendar-plan-${plan.tone}`} key={plan.id}>
                                    <span>{plan.duration} days</span><h3>{plan.title}</h3><p>{plan.description}</p>
                                    <div>
                                        <button onClick={() => installPlan(plan.id)}>
                                            {installedPlan === plan.id ? <Check size={17} /> : <Plus size={17} />}
                                            {activePlanIds.includes(plan.id) ? 'Restart this plan' : 'Add to calendar'}
                                        </button>
                                        {activePlanIds.includes(plan.id) && <button onClick={() => setEvents(removePrayerPlan(plan.id))}><Trash2 size={16} /> Remove</button>}
                                    </div>
                                </article>
                            ))}
                        </div>
                        <p className="calendar-google-note">
                            Exporting creates one `.ics` file containing every scheduled prayer. Google Calendar can import that file in one batch. Direct two-way sync requires a Google OAuth client configured for this app.{' '}
                            <a href="https://support.google.com/calendar/answer/37118" target="_blank" rel="noreferrer">Google import help</a>
                        </p>
                    </section>
                </div>
            )}

            {selectedDate && (
                <div className="calendar-overlay" onClick={() => setSelectedDate(null)}>
                    <section className="calendar-day-sheet" onClick={(event) => event.stopPropagation()}>
                        <header><div><small>Prayer focus</small><h2>{new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(fromDateKey(selectedDate))}</h2></div><button onClick={() => setSelectedDate(null)}><X size={20} /></button></header>
                        <div className="calendar-day-events">
                            {(eventsByDate[selectedDate] || []).map((event) => (
                                <article key={event.id}>
                                    <span>{event.source === 'plan' ? `Day ${event.day} of ${event.duration}` : 'Personal prayer'}</span>
                                    <h3>{event.title}</h3><p>{event.description}</p>{event.scripture && <small>{event.scripture}</small>}
                                    <footer>
                                        <a href={getGoogleCalendarUrl(event)} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Google Calendar</a>
                                        <button onClick={() => deleteEvent(event.id)}><Trash2 size={15} /> Remove</button>
                                    </footer>
                                </article>
                            ))}
                        </div>
                        <div className="calendar-add-event">
                            <p className="shared-kicker">Add your own focus</p>
                            <input value={newEvent.title} onChange={(event) => setNewEvent({ ...newEvent, title: event.target.value })} placeholder="Prayer topic" />
                            <textarea rows="3" value={newEvent.description} onChange={(event) => setNewEvent({ ...newEvent, description: event.target.value })} placeholder="A short prompt or intention" />
                            <input value={newEvent.scripture} onChange={(event) => setNewEvent({ ...newEvent, scripture: event.target.value })} placeholder="Related Scripture, if any" />
                            <button onClick={addPersonalEvent} disabled={!newEvent.title.trim()}><Plus size={17} /> Add to this day</button>
                        </div>
                    </section>
                </div>
            )}
        </ScreenLayout>
    );
};

export default Calendar;
